import { useState } from 'react';

import { Control, useController, UseControllerProps } from 'react-hook-form'

import { TextField, TextFieldProps } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export interface ITextInputProps extends Omit<UseControllerProps, 'control'> {
  id: any,
  name: string,
  shrink?: boolean,
  onChange?(event: React.ChangeEvent<HTMLInputElement>): any,
  onBlur?(event: React.FocusEvent<any>): any,
  control: Control<any, object>
  label?: string,
  step?: number,
  maxLength?: number,
  InputProps?: any,
  readOnly?: boolean,
  hiddenArrow?: boolean,
  required?: boolean,
}

export default function TextInput({ required, name, label, shrink, placeholder, hiddenArrow, defaultValue, type = "text", size = "small", readOnly = false, fullWidth = true, control, id, helperText, onChange, maxLength = Infinity, InputProps, onBlur, step = 1, ...other }: ITextInputProps & TextFieldProps) {

  const [isVisible, setIsVisible] = useState(false)

  const { field, fieldState } = useController({ control, name })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (type === 'numeric') {
      event.target.value = event.target.value?.toString()?.replace(/\D/g, '')
    }

    if (event.target.value?.toString()?.length > maxLength && type === 'number') {
      event.target.value = event.target.value?.toString().slice(0, maxLength)
    }

    if (onChange instanceof Function) {
      onChange(event)
    }

    field.onChange(event)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    if (type === 'numeric') {
      event.target.value = event.target.value?.toString()?.replace(/\D/g, '')
    }

    if (onBlur instanceof Function) {
      onBlur(event);
    }

    field.onBlur();
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (type === "number") {
      if (event.code === 'KeyE') {
        event.preventDefault();
      }
    }
  }

  const filterType = {
    text: 'text',
    number: 'number',
    numeric: 'text',
    date: 'date',
    time: 'time',
    password: 'password',
  }

  return (
    <TextField
      size='small'
      id={id}
      name={name}
      type={isVisible ? 'text' : filterType[type]}
      label={label}
      placeholder={placeholder}
      value={field.value || ''}
      // inputRef={field.ref}
      onChange={handleChange}
      onBlur={handleBlur}
      variant="outlined"
      error={!!fieldState?.error}
      helperText={fieldState?.error?.message || helperText}
      InputLabelProps={{ shrink: true }}
      sx={{
        ...(hiddenArrow && ({
          '& input::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          '& input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          }
        })
        ),
        ...(other.disabled && ({
          '& .MuiOutlinedInput-root': { bgcolor: 'grey.500' },
          '& .MuiInputLabel-root ': { bgcolor: 'common.white', px: 1 },
          '& .MuiOutlinedInput-notchedOutline  ': { border: 'none' },
        })),
        ...(required && ({
          '& .MuiInputLabel-root:after': {
            content: '" *"',
            color: 'error.main'
          },
        }))
      }}
      inputProps={{ ref: field.ref, maxLength: maxLength, onKeyPress: handleKeyPress }}
      InputProps={{ readOnly: readOnly, endAdornment: type === 'password' && (<InputAdornment position="end" sx={{ cursor: 'pointer' }} onClick={() => setIsVisible(!isVisible)}>{isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}</InputAdornment>), ...InputProps }}
      {...other}
    />
  );
}